import { useEffect, useState, useSyncExternalStore } from 'react'
import { Download, Share, X } from 'lucide-react'
import { LogoMark } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { track } from '@/lib/analytics'
import {
  getInstallMethod,
  promptInstall,
  subscribeInstallPrompt,
  type InstallMethod,
} from '@/lib/installPrompt'
import { cn } from '@/lib/utils'

const DISMISSED_KEY = 'fl-install-card-dismissed'

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Storage unavailable (private mode) — the card comes back next visit; harmless.
  }
}

function useInstallMethod(): InstallMethod {
  return useSyncExternalStore(subscribeInstallPrompt, getInstallMethod, () => null)
}

function InstallOffer({
  method,
  place,
  onDismiss,
  className,
}: {
  method: Exclude<InstallMethod, null>
  place: 'dashboard' | 'menu'
  onDismiss?: () => void
  className?: string
}) {
  useEffect(() => {
    track('install_prompt_shown', { place, method })
  }, [place, method])

  return (
    <section
      aria-label="Install the app"
      className={cn(
        'flex items-start gap-3 rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-3',
        className
      )}
    >
      <LogoMark size="sm" className="rounded-[10px]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[var(--fl-heading)]">Install Log My Spends</p>
        {method === 'ios' ? (
          <p className="mt-0.5 text-[13px] text-[var(--fl-text-2)]">
            Tap <strong className="font-semibold text-[var(--fl-text-1)]">Share</strong>{' '}
            <Share size={13} aria-label="(the share icon)" className="inline -mt-0.5" /> then{' '}
            <strong className="font-semibold text-[var(--fl-text-1)]">Add to Home Screen</strong> to
            open it like an app.
          </p>
        ) : (
          <>
            <p className="mt-0.5 text-[13px] text-[var(--fl-text-2)]">
              Opens full-screen from your home screen, like any other app.
            </p>
            <Button
              size="sm"
              onClick={() => {
                track('install_prompt_clicked', { place })
                void promptInstall()
              }}
              className="mt-2 h-8 gap-1.5 px-3"
            >
              <Download size={14} aria-hidden="true" /> Install
            </Button>
          </>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss install suggestion"
          className="shrink-0 rounded p-1 text-[var(--fl-text-3)] transition-colors hover:text-[var(--fl-text-1)]"
        >
          <X size={14} />
        </button>
      )}
    </section>
  )
}

/** Phone Dashboard card; once dismissed it's gone for good on this device (the Menu sheet
 *  keeps offering it). */
export function InstallAppCard({ className }: { className?: string }) {
  const method = useInstallMethod()
  const [dismissed, setDismissed] = useState(readDismissed)
  if (!method || dismissed) return null
  return (
    <InstallOffer
      method={method}
      place="dashboard"
      onDismiss={() => {
        writeDismissed()
        setDismissed(true)
      }}
      className={className}
    />
  )
}

/** The Menu sheet's always-there offer, until the app is installed. */
export function InstallAppRow({ className }: { className?: string }) {
  const method = useInstallMethod()
  if (!method) return null
  return <InstallOffer method={method} place="menu" className={className} />
}
